/**
 * Direct-to-GCS upload helper with client-side first-frame thumbnail extraction.
 *
 * Flow:
 *   1. Ask backend for a signed PUT URL  (getUploadUrl)
 *   2. PUT the file bytes directly to GCS (no API roundtrip)
 *   3. Extract a thumbnail in-browser:
 *        - video: HTMLVideoElement -> seek 0.1s -> canvas.drawImage -> JPEG blob
 *        - image: downsample via canvas -> JPEG blob
 *   4. PUT the thumbnail to its signed URL
 *   5. Backend finalize endpoint records the asset
 */
import { getUploadUrl, getResumableUrl, finalizeAsset, getChatUploadUrl, getMaterialResumableUrl, finalizeMaterial, type AssetKind, type MediaAsset, type ChatAttachment, type MaterialFacets } from './queries';
import { getLocationPhotoUploadUrl, finalizeLocationPhoto, type LocationPhoto } from './queries';

/** Files above this go through a Google resumable session (chunked, retried,
 *  resumable after a reload). Below it a file takes one signed PUT, retried: a
 *  2 MB photo gains nothing from a session, and opening one cost a round trip from
 *  the server to Google on every file of a 1,700-photo shoot (2026-09-26). */
export const RESUMABLE_MIN_BYTES = 8 * 1024 * 1024;
/** Chunk size must be a multiple of 256 KiB. 32 MiB keeps retries cheap on a
 *  bad connection and the request count sane on a 40 GB day. */
const DEFAULT_CHUNK = 32 * 1024 * 1024;
function chunkBytes(): number {
  const o = (window as unknown as { __kafChunk?: number }).__kafChunk;
  return o && o >= 262144 ? Math.floor(o / 262144) * 262144 : DEFAULT_CHUNK;
}
const RESUME_KEY = (creativeId: string, f: File) => `kafup:${creativeId}:${f.name}:${f.size}:${f.lastModified}`;


export interface UploadProgress {
  loaded: number;
  total: number;
  pct: number;
  stage: 'requesting' | 'uploading' | 'thumbnail' | 'finalizing' | 'done';
}

export interface UploadOptions {
  creativeId: string;
  /** upload into a clinic's raw material instead of a creative (migration 039) */
  material?: { clientId: string } & MaterialFacets;
  file: File;
  kind: AssetKind;
  category?: string;
  versionLabel?: string;
  notes?: string;
  onProgress?: (p: UploadProgress) => void;
  /** aborts the transfer. reason 'pause' keeps the resumable session, 'cancel' drops it */
  signal?: AbortSignal;
}

/** Thrown when a person paused or cancelled the file; never retried by itself. */
export class StoppedError extends Error {
  constructor(public reason: 'pause' | 'cancel') { super(reason === 'pause' ? 'Paused' : 'Cancelled'); }
}
function stopReason(signal?: AbortSignal): 'pause' | 'cancel' {
  return signal?.reason === 'pause' ? 'pause' : 'cancel';
}
function throwIfStopped(signal?: AbortSignal) {
  if (signal?.aborted) throw new StoppedError(stopReason(signal));
}
/** Abort an XHR with the signal; returns the unhook. */
function hook(xhr: XMLHttpRequest, signal: AbortSignal | undefined, reject: (e: unknown) => void): () => void {
  if (!signal) return () => {};
  const on = () => { xhr.abort(); reject(new StoppedError(stopReason(signal))); };
  if (signal.aborted) { on(); return () => {}; }
  signal.addEventListener('abort', on, { once: true });
  return () => signal.removeEventListener('abort', on);
}

export async function uploadAsset(opts: UploadOptions): Promise<MediaAsset> {
  if (opts.material) return uploadAssetResumable(opts);   // material always goes the resumable way
  if (opts.file.size >= RESUMABLE_MIN_BYTES) return uploadAssetResumable(opts);
  return uploadAssetSimple(opts);
}

/** Chunked upload to a Google resumable session. Survives dropped connections
 *  (each chunk retried with backoff, position re-queried from Google) and page
 *  reloads (the session is remembered per file for a week). */
export async function uploadAssetResumable(opts: UploadOptions): Promise<MediaAsset> {
  const { creativeId, file, kind, category, versionLabel, notes, onProgress, material, signal } = opts;
  const key = RESUME_KEY(material ? `material:${material.clientId}` : creativeId, file);
  // One opener for both homes: a creative's assets, or a clinic's raw material.
  const open = (type: string) => material
    ? getMaterialResumableUrl(material.clientId, { filename: file.name, content_type: type, size_bytes: file.size, doctor: material.doctor, procedure: material.procedure, date: material.date, tags: material.tags })
    : getResumableUrl(creativeId, { kind, category: kind === 'raw' ? (category ?? 'Any') : category   /* an edit's phase too: it used to be dropped, so every cut landed in the rough cut */, filename: file.name, content_type: type, size_bytes: file.size });
  let session: { assetId: string; sessionUrl: string; thumbnailUrl: string } | null = null;
  try { const raw = localStorage.getItem(key); if (raw) session = JSON.parse(raw); } catch { /* no storage */ }
  onProgress?.({ loaded: 0, total: file.size, pct: 0, stage: 'requesting' });
  if (!session) {
    const r = await open(file.type || 'application/octet-stream');
    session = { assetId: r.asset_id, sessionUrl: r.session_url, thumbnailUrl: r.thumbnail_upload_url };
    try { localStorage.setItem(key, JSON.stringify(session)); } catch { /* fine */ }
  }
  const type = file.type || 'application/octet-stream';
  let offset = await queryOffset(session.sessionUrl, file.size);
  if (offset === -2) {
    // the session is gone (expired or rejected): start a fresh one, once
    try { localStorage.removeItem(key); } catch { /* fine */ }
    const r = await open(type);
    session = { assetId: r.asset_id, sessionUrl: r.session_url, thumbnailUrl: r.thumbnail_upload_url };
    try { localStorage.setItem(key, JSON.stringify(session)); } catch { /* fine */ }
    offset = 0;
  }
  const chunk = chunkBytes();
  try {
    while (offset >= 0 && offset < file.size) {
      throwIfStopped(signal);
      const end = Math.min(offset + chunk, file.size) - 1;
      const res = await putChunkWithRetry(session.sessionUrl, file.slice(offset, end + 1), type, offset, end, file.size, (sent) => {
        const loaded = offset + sent;
        onProgress?.({ loaded, total: file.size, pct: Math.min(99, Math.round((loaded / file.size) * 100)), stage: 'uploading' });
      }, signal);
      if (res.done) { offset = file.size; break; }
      offset = res.nextOffset;
    }
    throwIfStopped(signal);
  } catch (e) {
    // Cancelled: forget the session so going again starts clean. Paused: keep it.
    if (e instanceof StoppedError && e.reason === 'cancel') { try { localStorage.removeItem(key); } catch { /* fine */ } }
    throw e;
  }
  onProgress?.({ loaded: file.size, total: file.size, pct: 99, stage: 'finalizing' });
  let hasThumbnail = false; let width: number | undefined; let height: number | undefined; let durationSec: number | undefined;
  try {
    if (type.startsWith('video/')) {
      const t = await videoFirstFrameJpeg(file);
      await putBlob(session.thumbnailUrl, t.blob, 'image/jpeg'); hasThumbnail = true; width = t.width; height = t.height; durationSec = t.durationSec;
    } else if (type.startsWith('image/')) {
      const t = await imageDownsampleJpeg(file);
      await putBlob(session.thumbnailUrl, t.blob, 'image/jpeg'); hasThumbnail = true; width = t.width; height = t.height;
    }
  } catch { /* a raw without a thumbnail is still a raw */ }
  const asset = material
    ? await finalizeMaterial(session.assetId, { width, height, duration_sec: durationSec, notes, has_thumbnail: hasThumbnail })
    : await finalizeAsset(creativeId, session.assetId, { width, height, duration_sec: durationSec, version_label: versionLabel, notes, has_thumbnail: hasThumbnail });
  try { localStorage.removeItem(key); } catch { /* fine */ }
  onProgress?.({ loaded: file.size, total: file.size, pct: 100, stage: 'done' });
  return asset;
}

/** Ask Google how much of the file it holds. -1 = nothing yet, -2 = session gone, size = complete. */
async function queryOffset(sessionUrl: string, size: number): Promise<number> {
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      const r = await fetch(sessionUrl, { method: 'PUT', headers: { 'Content-Range': `bytes */${size}` } });
      if (r.status === 200 || r.status === 201) return size;
      if (r.status === 308) {
        const range = r.headers.get('Range');
        const m = range && /bytes=0-(\d+)/.exec(range);
        return m ? Number(m[1]) + 1 : 0;
      }
      if (r.status === 404 || r.status === 410 || r.status === 400) return -2;
    } catch { /* network: retry */ }
    await sleep(500 * 2 ** attempt);
  }
  return 0;
}

async function putChunkWithRetry(sessionUrl: string, part: Blob, type: string, start: number, end: number, total: number,
  onSent: (sent: number) => void, signal?: AbortSignal): Promise<{ done: boolean; nextOffset: number }> {
  let lastErr: unknown = null;
  for (let attempt = 0; attempt < 7; attempt++) {
    try {
      return await putChunk(sessionUrl, part, type, start, end, total, onSent, signal);
    } catch (e) {
      if (e instanceof StoppedError) throw e;
      lastErr = e;
      // Ask Google where we really are before trying again: a chunk may have landed
      // even though the response never reached us.
      await sleep(Math.min(30000, 800 * 2 ** attempt));
      const off = await queryOffset(sessionUrl, total);
      if (off === total) return { done: true, nextOffset: total };
      if (off === -2) throw new Error('Upload session expired; start this file again');
      if (off > start) return { done: false, nextOffset: off };
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('Upload failed after several tries');
}

function putChunk(sessionUrl: string, part: Blob, type: string, start: number, end: number, total: number, onSent: (sent: number) => void, signal?: AbortSignal): Promise<{ done: boolean; nextOffset: number }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const unhook = hook(xhr, signal, reject);
    xhr.onloadend = unhook;
    xhr.open('PUT', sessionUrl);
    xhr.setRequestHeader('Content-Type', type);
    xhr.setRequestHeader('Content-Range', `bytes ${start}-${end}/${total}`);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onSent(e.loaded); };
    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 201) { resolve({ done: true, nextOffset: total }); return; }
      if (xhr.status === 308) {
        const range = xhr.getResponseHeader('Range');
        const m = range && /bytes=0-(\d+)/.exec(range);
        resolve({ done: false, nextOffset: m ? Number(m[1]) + 1 : end + 1 }); return;
      }
      reject(new Error(`Google answered ${xhr.status} on a chunk`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.ontimeout = () => reject(new Error('Chunk timed out'));
    xhr.timeout = 10 * 60 * 1000;
    xhr.send(part);
  });
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function uploadAssetSimple(opts: UploadOptions): Promise<MediaAsset> {
  const { creativeId, file, kind, category, versionLabel, notes, onProgress, signal } = opts;

  onProgress?.({ loaded: 0, total: file.size, pct: 0, stage: 'requesting' });

  // 1. Sign URL
  const signed = await getUploadUrl(creativeId, {
    kind,
    category: kind === 'raw' ? (category ?? 'Any') : category   /* an edit's phase too: it used to be dropped, so every cut landed in the rough cut */,
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
    size_bytes: file.size,
  });

  // 2. Upload file with progress. A small file is simply sent again if the line drops.
  for (let attempt = 0; ; attempt++) {
    try {
      await putWithProgress(signed.upload_url, file, file.type || 'application/octet-stream', (loaded, total) => {
        onProgress?.({
          loaded, total,
          pct: total ? Math.round((loaded / total) * 90) : 0, // reserve 10% for thumb + finalize
          stage: 'uploading',
        });
      }, signal);
      break;
    } catch (e) {
      if (e instanceof StoppedError) throw e;
      if (attempt >= 3 || (e instanceof Error && /^Upload failed: 4\d\d/.test(e.message))) throw e;
      await sleep(1000 * 2 ** attempt);
    }
  }

  throwIfStopped(signal);
  // 3+4. Thumbnail (best-effort)
  let hasThumb = false;
  let dims: { width?: number; height?: number; durationSec?: number } = {};
  try {
    onProgress?.({ loaded: file.size, total: file.size, pct: 92, stage: 'thumbnail' });
    if (file.type.startsWith('video/')) {
      const { blob, width, height, durationSec } = await videoFirstFrameJpeg(file);
      await putBlob(signed.thumbnail_upload_url, blob, 'image/jpeg');
      dims = { width, height, durationSec };
      hasThumb = true;
    } else if (file.type.startsWith('image/')) {
      const { blob, width, height } = await imageDownsampleJpeg(file);
      await putBlob(signed.thumbnail_upload_url, blob, 'image/jpeg');
      dims = { width, height };
      hasThumb = true;
    }
    // PDFs / docs -> no thumb (icon fallback in UI)
  } catch (err) {
    // Don't fail the whole upload because thumbnail extraction broke
    console.warn('Thumbnail extraction failed:', err);
  }

  // 5. Finalize
  onProgress?.({ loaded: file.size, total: file.size, pct: 96, stage: 'finalizing' });
  const asset = await finalizeAsset(creativeId, signed.asset_id, {
    width: dims.width,
    height: dims.height,
    duration_sec: dims.durationSec,
    version_label: versionLabel,
    notes,
    has_thumbnail: hasThumb,
  });

  onProgress?.({ loaded: file.size, total: file.size, pct: 100, stage: 'done' });
  return asset;
}

// ---- helpers ----

function putWithProgress(url: string, file: File, contentType: string, onProgress: (loaded: number, total: number) => void, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const unhook = hook(xhr, signal, reject);
    xhr.onloadend = unhook;
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status} ${xhr.responseText.slice(0, 200)}`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.ontimeout = () => reject(new Error('Upload timed out'));
    xhr.timeout = 10 * 60 * 1000;
    xhr.send(file);
  });
}

async function putBlob(url: string, blob: Blob, contentType: string): Promise<void> {
  const r = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: blob,
  });
  if (!r.ok) throw new Error(`Thumbnail upload failed: ${r.status}`);
}

interface VideoFrame { blob: Blob; width: number; height: number; durationSec: number }

/** How long the browser may spend on a preview frame before the upload goes on without
 *  one. Some camera masters (Sony 10-bit 4:2:2) never decode in Chrome: no error, no
 *  metadata, the promise just never settles. Before this limit the upload sat at
 *  "finalizing" for ever although every byte was in storage (Farouk, 2026-10-21).
 *  The server makes the missing thumbnail afterwards (image_preview.py). */
const PREVIEW_TIMEOUT_MS = 12000;

function withTimeout<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
  let t: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    p,
    new Promise<never>((_, reject) => { t = setTimeout(() => reject(new Error(`${what} took longer than ${ms / 1000}s`)), ms); }),
  ]).finally(() => clearTimeout(t));
}

function videoFirstFrameJpeg(file: File): Promise<VideoFrame> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  const work = (async () => {
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.src = url;
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('Could not load video metadata'));
    });
    // metadata without a decodable picture (unsupported codec) reports 0x0
    if (!video.videoWidth || !video.videoHeight) throw new Error('Browser cannot decode this video');
    // Seek to ~0.1s for a non-black first frame
    await new Promise<void>((resolve, reject) => {
      video.onseeked = () => resolve();
      video.onerror = () => reject(new Error('Could not seek video'));
      video.currentTime = Math.min(0.1, video.duration / 2 || 0.1);
    });

    const canvas = document.createElement('canvas');
    const targetW = Math.min(800, video.videoWidth);
    const ratio = targetW / video.videoWidth;
    canvas.width = targetW;
    canvas.height = Math.round(video.videoHeight * ratio);
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', 0.85)
    );
    return { blob, width: video.videoWidth, height: video.videoHeight, durationSec: video.duration };
  })();
  return withTimeout(work, PREVIEW_TIMEOUT_MS, 'Video preview').finally(() => {
    // stop the decoder and let go of the file, whether it worked, failed or timed out
    video.onloadedmetadata = video.onseeked = video.onerror = null;
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  });
}

interface ImageDims { blob: Blob; width: number; height: number }

function imageDownsampleJpeg(file: File): Promise<ImageDims> {
  return withTimeout(imageDownsample(file), PREVIEW_TIMEOUT_MS, 'Image preview');
}

async function imageDownsample(file: File): Promise<ImageDims> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Could not load image'));
    });
    const canvas = document.createElement('canvas');
    const targetW = Math.min(800, img.naturalWidth);
    const ratio = targetW / img.naturalWidth;
    canvas.width = targetW;
    canvas.height = Math.round(img.naturalHeight * ratio);
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', 0.85)
    );
    return { blob, width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** A chat attachment of any type: signed PUT under 8 MB, a Google resumable
 *  session above it (chunked, retried). Returns what the message stores. */
export async function uploadChatFile(file: File, onProgress?: (pct: number) => void): Promise<ChatAttachment> {
  const type = file.type || 'application/octet-stream';
  const r = await getChatUploadUrl({ filename: file.name, content_type: type, size_bytes: file.size });
  if (!r.resumable) {
    await putWithProgress(r.upload_url, file, type, (loaded, total) => onProgress?.(Math.round((loaded / total) * 100)));
  } else {
    let offset = 0;
    const chunk = chunkBytes();
    while (offset < file.size) {
      const end = Math.min(offset + chunk, file.size) - 1;
      const res = await putChunkWithRetry(r.upload_url, file.slice(offset, end + 1), type, offset, end, file.size,
        (sent) => onProgress?.(Math.round(((offset + sent) / file.size) * 100)));
      if (res.done) break;
      offset = res.nextOffset;
    }
  }
  onProgress?.(100);
  return { path: r.path, name: file.name, content_type: type, size: file.size };
}

/** A clinic location photo: signed PUT, browser-made thumbnail, finalize.
 *  Small images only, so no resumable session. */
export async function uploadLocationPhoto(clientId: string, locationId: string, file: File, onProgress?: (pct: number) => void): Promise<LocationPhoto> {
  const type = file.type || 'image/jpeg';
  const r = await getLocationPhotoUploadUrl(clientId, locationId, { filename: file.name, content_type: type, size_bytes: file.size });
  await putWithProgress(r.upload_url, file, type, (loaded, total) => onProgress?.(total ? Math.round((loaded / total) * 90) : 0));
  let hasThumb = false;
  let dims: { width?: number; height?: number } = {};
  try {
    const t = await imageDownsampleJpeg(file);
    await putBlob(r.thumbnail_upload_url, t.blob, 'image/jpeg');
    hasThumb = true; dims = { width: t.width, height: t.height };
  } catch (err) {
    console.warn('Thumbnail extraction failed:', err);
  }
  const photo = await finalizeLocationPhoto(clientId, locationId, r.photo_id, { ...dims, has_thumbnail: hasThumb });
  onProgress?.(100);
  return photo;
}
